import ClassHoldingsOverview from './ClassHoldings/ClassHoldingsOverview';
import CompanyPerformanceOverview from './CompanyDetails/CompanyPerformanceOverview';
import ErrorBoundary from 'components/ErrorBoundary';
import { Divider, Typography } from '@mui/material';
import { Vehicle } from 'further-types/lp/vehicle';
import PageLoader from 'components/PageLoader';
import { Column, Row } from 'further-ui/components/Layout';
import FormField from 'further-ui/components/FormField';
import { numberToDisplayString } from 'further-ui/utils';
import InvestmentHistory from './ClassHoldings/InvestmentHistory';
import RevaluationHistory from './ClassHoldings/RevaluationHistory';
import { ApiResponses } from 'lib/trpcClient';

type Props = {
  selectedVehicle: Vehicle;
  companyId: string;
  assetPerformanceAggregations:
    | ApiResponses['asset']['getAssetPerformance']
    | undefined;
  isAssetPerformanceLoading: boolean;
};

const CompanyOverview: React.FC<Props> = ({
  selectedVehicle,
  companyId,
  assetPerformanceAggregations,
  isAssetPerformanceLoading,
}) => {
  if (isAssetPerformanceLoading) {
    return <PageLoader />;
  }

  return (
    <Column spacing="lg">
      <Row fullWidth>
        <FormField spacing="xs" label="Initial investment date">
          <Typography variant="body2">
            {assetPerformanceAggregations?.firstInvestmentDate?.toReadable() ??
              '--'}
          </Typography>
        </FormField>
        <FormField spacing="xs" label="Last investment date">
          <Typography variant="body2">
            {assetPerformanceAggregations?.lastInvestmentDate?.toReadable() ??
              '--'}
          </Typography>
        </FormField>
        <FormField spacing="xs" label="Holding period">
          <Typography variant="body2">
            {assetPerformanceAggregations?.holdingPeriod != null
              ? numberToDisplayString(
                  assetPerformanceAggregations.holdingPeriod,
                  {
                    style: 'unit',
                    unit: 'day',
                    unitDisplay: 'long',
                  },
                )
              : '--'}
          </Typography>
        </FormField>
        <FormField spacing="xs" label="Last revaluation date">
          <Typography variant="body2">
            {assetPerformanceAggregations?.lastRevaluationDate?.toReadable() ??
              '--'}
          </Typography>
        </FormField>
      </Row>
      <Divider />
      {assetPerformanceAggregations && (
        <ErrorBoundary>
          <CompanyPerformanceOverview
            selectedVehicle={selectedVehicle}
            assetPerformanceAggregations={assetPerformanceAggregations}
          />
        </ErrorBoundary>
      )}
      <Divider />
      <ClassHoldingsOverview
        vehicleId={selectedVehicle._id}
        companyId={companyId}
        vehicleCurrencyCode={selectedVehicle.baseCurrency}
      />
      <Divider />
      {companyId && (
        <ErrorBoundary>
          <InvestmentHistory
            vehicleId={selectedVehicle._id}
            companyId={companyId}
          />
        </ErrorBoundary>
      )}
      {companyId && (
        <>
          <Divider />
          <ErrorBoundary>
            <RevaluationHistory
              vehicleId={selectedVehicle._id}
              companyId={companyId}
            />
          </ErrorBoundary>
        </>
      )}
    </Column>
  );
};

export default CompanyOverview;
